import React from 'react';

/**
 * Пропсы для ссылки, текст которой должны уметь выделять мышью.
 *
 * Chrome не даёт выделять текст протяжкой, начатой на `<a href>`: нажатие на
 * ссылку он трактует как начало её перетаскивания. `draggable={false}` убирает
 * перетаскивание, но выделение от этого не появляется — получается ссылка, на
 * которой протяжка не делает вообще ничего, а по отпусканию кнопки происходит
 * переход. Проверено: стоит снять у элемента `href` — и выделение работает.
 *
 * Поэтому на время протяжки левой кнопкой `href` снимаем, а на `mouseup`
 * возвращаем. Ссылка при этом остаётся полноценной: `href` на месте всё
 * остальное время, так что работают и Ctrl/⌘+клик, и клик колесом, и «открыть
 * в новой вкладке» с «копировать адрес ссылки» из контекстного меню — для них
 * `href` мы не трогаем, потому что снимаем его только для немодифицированного
 * клика левой кнопкой.
 *
 * Переход по обычному клику делает не `href`, а react-router (`Link` сам зовёт
 * `preventDefault` и толкает историю), поэтому снятие атрибута навигацию не
 * ломает.
 */

/** Ссылка, у которой сейчас снят `href`, и его исходное значение. */
let pending: { element: HTMLAnchorElement; href: string } | null = null;

const restoreHref = () => {
  if (pending === null) return;
  const { element, href } = pending;
  pending = null;
  window.removeEventListener('mouseup', restoreHref);
  element.setAttribute('href', href);
};

const isModifiedEvent = (event: React.MouseEvent) =>
  event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;

export const selectableLinkProps = {
  draggable: false,

  onMouseDown: (event: React.MouseEvent<HTMLElement>) => {
    if (event.button !== 0 || isModifiedEvent(event)) return;
    // Предыдущую протяжку могли не завершить внутри окна — подчищаем.
    restoreHref();
    const element = event.currentTarget;
    if (!(element instanceof HTMLAnchorElement)) return;
    const href = element.getAttribute('href');
    if (href === null) return;
    pending = { element, href };
    element.removeAttribute('href');
    // Слушаем на window, а не на самой ссылке: кнопку часто отпускают, уведя
    // курсор за её пределы, и тогда `mouseup` до ссылки не доходит.
    window.addEventListener('mouseup', restoreHref);
  },

  onClick: (event: React.MouseEvent<HTMLElement>) => {
    // `mouseup` приходит раньше `click`, так что здесь `href` уже на месте;
    // подстраховываемся на случай синтетического клика без `mouseup`.
    restoreHref();
    const selection = window.getSelection();
    if (selection === null || selection.isCollapsed) return;
    if (selection.toString().trim() === '') return;
    // Проверяем, что выделение задело именно эту ссылку: постороннее выделение
    // где-то ещё на странице блокировать переход не должно.
    if (!selection.containsNode(event.currentTarget, true)) return;
    // Протяжка завершилась выделением — это не переход. Иначе навигация унесёт
    // со страницы вместе с только что выделенным текстом.
    event.preventDefault();
  },
};
